import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IssueVoucher,
  ListVouchersQuery,
  RedeemVoucher,
  Voucher,
  VoucherBalance,
  VoucherLiabilityReport,
  VoucherStatus,
  VoucherWithLedger,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS9 phase 2 — vouchers & gift cards on the double-entry liability ledger (brief 09 §2; ADR-0009 +
 * ADR-0008). TanStack Query over the generated client, mirroring data/services.ts / data/policy.ts.
 *
 * HONESTY (non-negotiable): balances and redemption amounts come from the API — the UI DISPLAYS the
 * server's derived `balancePence` and POSTs intent; it never recomputes a balance or a redeemed total
 * locally. Redemption is a TENDER at checkout (full or partial against the balance), never a discount
 * line. Issue/redeem/void are IDEMPOTENT on a client-supplied `idempotencyKey`: the caller mints the
 * key once per intent and gates the button on `isPending`, so a double-tap is a safe no-op (a replay
 * moves no total) — the UI must still not invite a second tap. 422/409 surface faithfully through the
 * shared ApiClientError envelope.
 *
 * Single-purpose vouchers are deferred server-side (issue rejects `purpose=single` with 422); the issue
 * FORM must only offer the multi-purpose/gift-card option — this data layer stays faithful to the typed
 * body and does not itself restrict the choice.
 */

export const VOUCHERS_PAGE = 50;

export interface VoucherPage {
  data: Voucher[];
  pagination: { limit: number; offset: number; total: number };
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

export function useVouchers(status?: VoucherStatus, offset = 0) {
  return useQuery({
    queryKey: qk.vouchers(status ?? null, offset),
    queryFn: async (): Promise<VoucherPage> =>
      unwrap(
        await api.GET('/v1/vouchers', {
          params: { query: { status, limit: VOUCHERS_PAGE, offset } as ListVouchersQuery },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** One voucher with its full append-only ledger + derived balance (the staff/till detail view). */
export function useVoucher(id: string | null) {
  return useQuery({
    queryKey: id ? qk.voucher(id) : ['voucher', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<VoucherWithLedger> =>
      unwrap(await api.GET('/v1/vouchers/{id}', { params: { path: { id: id! } } })),
  });
}

/**
 * Staff/till balance lookup by voucher code (the till's "scan/type a code" step). Returns null on 404
 * (unknown/void code) so the till can show "no such voucher" cleanly rather than surfacing an error.
 * Gated on a code so an empty field doesn't fire the query; not cached aggressively — the balance is a
 * live number the till re-reads.
 */
export function useVoucherBalanceByCode(code: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.voucherLookup(code ?? ''),
    enabled: Boolean(code) && enabled,
    staleTime: 0,
    queryFn: async (): Promise<VoucherBalance | null> => {
      const res = await api.GET('/v1/vouchers/lookup/{code}', {
        params: { path: { code: code! } },
      });
      if (res.response.status === 404) return null;
      return unwrap(res);
    },
  });
}

/**
 * The gift-card liability reconciliation report (ADR-0009 §reconciliation invariant): sold / redeemed /
 * expired / outstanding, with `reconciles` asserting they net to the penny. Manager-only on the API.
 */
export function useVoucherLiabilityReport(enabled = true) {
  return useQuery({
    queryKey: qk.voucherLiability,
    enabled,
    queryFn: async (): Promise<VoucherLiabilityReport> =>
      unwrap(await api.GET('/v1/vouchers/liability-report')),
  });
}

// ── Mutations (all idempotent; gate the button on `isPending`) ──────────────────────

/** Issue (sell or comp) a voucher. The caller mints `idempotencyKey` once per intent. */
export function useIssueVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IssueVoucher): Promise<Voucher> =>
      unwrap(await api.POST('/v1/vouchers', { body: body as never })),
    onSuccess: () => invalidateVouchers(qc),
  });
}

/**
 * Redeem against a voucher's balance — full or partial; a TENDER, not a discount. Idempotent on
 * `idempotencyKey`: a re-delivered redeem returns the original result and moves no total. Returns the
 * voucher with its updated ledger + balance so the till shows the new (server-derived) balance.
 */
export function useRedeemVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: RedeemVoucher }): Promise<VoucherWithLedger> =>
      unwrap(
        await api.POST('/v1/vouchers/{id}/redeem', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: (voucher) => {
      qc.setQueryData(qk.voucher(voucher.id), voucher);
      invalidateVouchers(qc);
    },
  });
}

/**
 * Run the salon-scoped breakage sweep (ADR-0009): expire every still-active voucher whose `expiresAt` has
 * passed, writing a balanced breakage event (debit gift_card_liability, credit breakage_income) per voucher.
 * Manager-only on the API. Takes NO body (the salon scope is server-resolved from the session) and is safe to
 * re-run — a voucher already flipped to 'expired' is no longer selected, so a re-run recognises no extra
 * breakage. Returns the server's `{ expiredCount, breakagePence }` (server figures — never recomputed here).
 * onSuccess re-bases the voucher list AND the liability totals that the sweep just shifted.
 */
export function useExpireVouchers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ expiredCount: number; breakagePence: number }> =>
      unwrap(await api.POST('/v1/vouchers/expire')),
    onSuccess: () => invalidateVouchers(qc),
  });
}

/** Void a voucher — clears its remaining liability to comp_expense, never silently (reason required). */
export function useVoidVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      reason: string;
      idempotencyKey: string;
    }): Promise<VoucherWithLedger> =>
      unwrap(
        await api.POST('/v1/vouchers/{id}/void', {
          params: { path: { id: input.id } },
          body: { reason: input.reason, idempotencyKey: input.idempotencyKey },
        }),
      ),
    onSuccess: (voucher) => {
      qc.setQueryData(qk.voucher(voucher.id), voucher);
      invalidateVouchers(qc);
    },
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/** Any issue/redeem/void shifts the list and the salon-wide liability totals. */
function invalidateVouchers(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['vouchers'] });
  void qc.invalidateQueries({ queryKey: qk.voucherLiability });
}
